import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ISwipableAction } from "../types/ItemsList.types";

export const useItemsList = <TItem extends { [key: string]: any }>(
  items?: TItem[],
  swipeLeftActions?: ISwipableAction<TItem>[],
  swipeRightActions?: ISwipableAction<TItem>[]
) => {
  const [filteredItems, setFilteredItems] = useState<TItem[] | undefined>(
    items
  );
  const [searchValue, setSearchValue] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    onSearch(searchValue);
  }, [items]);

  const onEdit = (id: number) => {
    const editAction = findEditAction();

    if (editAction?.endpoint) {
      navigate(editAction.endpoint.replace(":id", id.toString()));
    } else if (editAction?.permissionRoute) {
      navigate(editAction.permissionRoute.replace(":id", id.toString()));
    }
  };

  const onSearch = (searchValue: string) => {
    setSearchValue(searchValue);

    if (!searchValue.trim()) {
      setFilteredItems(items);
      return;
    }

    const normalizedSearch = searchValue.toLowerCase().replace(/\s+/g, " ").trim();

    const filtered = items?.filter((item) => {
      const itemString = flattenObjectValues(item);
      return itemString.includes(normalizedSearch);
    });

    setFilteredItems(filtered);
  };

  const findEditAction = (): ISwipableAction<TItem> | undefined => {
    return (
      swipeLeftActions?.find((x) => x.type === "edit") ??
      swipeRightActions?.find((x) => x.type === "edit")
    );
  };

  const flattenObjectValues = (obj: any): string => {
    const values: string[] = [];

    const extractValues = (val: any) => {
      if (val == null) return;

      if (typeof val === "object") {
        for (const key in val) {
          extractValues(val[key]);
        }
      } else if (typeof val === "string" && isIsoDate(val)) {
        values.push(formatDate(val));
      } else {
        values.push(String(val));
      }
    };

    extractValues(obj);
    return values.join(" ").replace(/\s+/g, " ").toLowerCase();
  };

  const isIsoDate = (val: string): boolean => {
    const isoDateRegex =
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|([+-]\d{2}:\d{2}))?$/;
    return isoDateRegex.test(val);
  };

  const formatDate = (isoString: string): string => {
    const date = new Date(isoString);
    const dd = String(date.getDate()).padStart(2, "0");
    const MM = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    return `${dd}.${MM}.${yyyy} ${hh}:${mm}`;
  };

  return {
    filteredItems,
    onEdit,
    onSearch,
  };
};
